import { ExpandMore, X as XIcon } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { CONTENT_TYPES } from "../../constants/contentTypes";
import { hostname } from "../../utils";
import { INTEGRATION_LOGOS } from "../integrations/constants";

export default ({
  value,
  onChange,
  companyId,
  getToken,
}: {
  value: any;
  onChange: (value: any) => void;
  companyId?: string;
  getToken?: Function;
}) => {
  const [wordpressIntegrations, setWordpressIntegrations] = useState<any[]>([]);
  const [linkedinIntegrations, setLinkedinIntegrations] = useState<any[]>([]);
  const [facebookIntegrations, setFacebookIntegrations] = useState<any[]>([]);
  const [instagramIntegrations, setInstagramIntegrations] = useState<any[]>([]);
  const [marketingCampaignTemplates, setMarketingCampaignTemplates] = useState<
    any[]
  >([]);

  const contentTypes = CONTENT_TYPES.map((type) => ({
    name: type.name,
    icon: type.name === "x_post" ? <XIcon /> : type.icon,
    title: type.title,
    subSettings: type.subSettings || [],
  }));

  // Helper function to get logo for integration type
  const getIntegrationLogo = (integrationType: string) => {
    return INTEGRATION_LOGOS[integrationType?.toLowerCase()] || null;
  };

  // Fetch blog integrations (WordPress/Shopify) for blog post settings
  useEffect(() => {
    const fetchWordpressIntegrations = async () => {
      if (!companyId || !getToken) return;

      try {
        const token = await getToken();
        const response = await axios.get(
          `${hostname}/integrations/by-type/blog`,
          {
            params: { companyId },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setWordpressIntegrations(response.data.items || []);
      } catch (error) {
        console.error("Error fetching blog integrations:", error);
        setWordpressIntegrations([]); // Set empty array on error
      }
    };

    fetchWordpressIntegrations();
  }, [companyId, getToken]);

  // Fetch LinkedIn integrations for LinkedIn post settings
  useEffect(() => {
    const fetchLinkedinIntegrations = async () => {
      if (!companyId || !getToken) return;

      try {
        const token = await getToken();
        const response = await axios.get(
          `${hostname}/integrations/by-type/linkedin`,
          {
            params: { companyId },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setLinkedinIntegrations(response.data.items || []);
      } catch (error) {
        console.error("Error fetching LinkedIn integrations:", error);
        setLinkedinIntegrations([]); // Set empty array on error
      }
    };

    fetchLinkedinIntegrations();
  }, [companyId, getToken]);

  // Fetch Facebook integrations for Facebook post settings
  useEffect(() => {
    const fetchFacebookIntegrations = async () => {
      if (!companyId || !getToken) return;

      try {
        const token = await getToken();
        const response = await axios.get(
          `${hostname}/integrations/by-type/meta`,
          {
            params: { companyId, type: "facebook" },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setFacebookIntegrations(response.data.items || []);
      } catch (error) {
        console.error("Error fetching Facebook integrations:", error);
        setFacebookIntegrations([]); // Set empty array on error
      }
    };

    fetchFacebookIntegrations();
  }, [companyId, getToken]);

  // Fetch Instagram integrations for Instagram post settings
  useEffect(() => {
    const fetchInstagramIntegrations = async () => {
      if (!companyId || !getToken) return;

      try {
        const token = await getToken();
        const response = await axios.get(
          `${hostname}/integrations/by-type/meta`,
          {
            params: { companyId, type: "instagram" },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setInstagramIntegrations(response.data.items || []);
      } catch (error) {
        console.error("Error fetching Instagram integrations:", error);
        setInstagramIntegrations([]); // Set empty array on error
      }
    };

    fetchInstagramIntegrations();
  }, [companyId, getToken]);

  // Fetch Marketing Campaign templates for marketing_email settings
  useEffect(() => {
    const fetchMarketingCampaignTemplates = async () => {
      if (!companyId || !getToken) return;

      try {
        const token = await getToken();
        const response = await axios.get(
          `${hostname}/marketing-campaign-templates`,
          {
            params: { companyId },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setMarketingCampaignTemplates(response.data?.items || []);
      } catch (error) {
        console.error("Error fetching marketing campaign templates:", error);
        setMarketingCampaignTemplates([]);
      }
    };

    fetchMarketingCampaignTemplates();
  }, [companyId, getToken]);

  // Ensure value is always an object, even if null/undefined is passed
  const safeValue = value || {};

  return (
    <div>
      {contentTypes.map((contentType) => (
        <Accordion key={contentType.name} sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <FormControlLabel
              key={contentType.title}
              control={
                <Checkbox
                  checked={safeValue[contentType.name]?.enabled || false}
                  onChange={() =>
                    onChange({
                      ...safeValue,
                      [contentType.name]: {
                        ...safeValue[contentType.name],
                        enabled: !(safeValue[contentType.name]?.enabled || false),
                      },
                    })
                  }
                  name={contentType.name}
                  color="primary"
                  onClick={(e) => e.stopPropagation()}
                />
              }
              label={
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  {contentType.icon}
                  <Typography variant="subtitle1">
                    {contentType.title}
                  </Typography>
                </div>
              }
            />
          </AccordionSummary>
          <AccordionDetails>
            {contentType.subSettings.map((setting: any) => {
              if (setting.type === "select") {
                // For blog integration select, use fetched blog integrations (WordPress/Shopify)
                // For LinkedIn integration select, use fetched LinkedIn integrations
                // For Meta integration select, use fetched Meta integrations (Facebook/Instagram)
                let options = setting.options || [];
                if (setting.name === "wordpressIntegration") {
                  options = wordpressIntegrations.map((wp) => ({
                    value: wp.id,
                    label: wp.name,
                  }));
                } else if (setting.name === "linkedinIntegration") {
                  options = linkedinIntegrations.map((li) => ({
                    value: li.id,
                    label: li.name,
                  }));
                } else if (setting.name === "facebookIntegration") {
                  options = facebookIntegrations.map((fb) => ({
                    value: fb.id,
                    label: fb.name,
                  }));
                } else if (setting.name === "instagramIntegration") {
                  options = instagramIntegrations.map((ig) => ({
                    value: ig.id,
                    label: ig.name,
                  }));
                } else if (setting.name === "marketingCampaignTemplate") {
                  options = marketingCampaignTemplates.map((t) => ({
                    value: t.id,
                    label: t.name,
                  }));
                }

                return (
                  <FormControl key={setting.name} fullWidth sx={{ mb: 2 }}>
                    <InputLabel>{setting.label}</InputLabel>
                    <Select
                      value={
                        safeValue[contentType.name]?.subSettings?.[setting.name] ||
                        ""
                      }
                      onChange={(e) =>
                        onChange({
                          ...safeValue,
                          [contentType.name]: {
                            ...safeValue[contentType.name],
                            subSettings: {
                              ...safeValue[contentType.name]?.subSettings,
                              [setting.name]: e.target.value,
                            },
                          },
                        })
                      }
                      label={setting.label}
                      renderValue={(selected) => {
                        const selectedIntegration = options.find(
                          (opt: any) => opt.value === selected
                        );
                        if (!selectedIntegration) return "";

                        // Get the full integration object to access the type and page info
                        let integration: any = null;
                        if (setting.name === "wordpressIntegration") {
                          integration = wordpressIntegrations.find(
                            (wp) => wp.id === selected
                          );
                        } else if (setting.name === "linkedinIntegration") {
                          integration = linkedinIntegrations.find(
                            (li) => li.id === selected
                          );
                        } else if (setting.name === "facebookIntegration") {
                          integration = facebookIntegrations.find(
                            (fb) => fb.id === selected
                          );
                        } else if (setting.name === "instagramIntegration") {
                          integration = instagramIntegrations.find(
                            (ig) => ig.id === selected
                          );
                        }

                        const integrationType = integration?.type || "";
                        const pageInfo = integration?.pageInfo;
                        const pagePicture =
                          pageInfo?.pagePicture || pageInfo?.profilePicture;
                        const logo = pagePicture || getIntegrationLogo(integrationType);
                        const isSocialIntegration =
                          setting.name === "facebookIntegration" ||
                          setting.name === "instagramIntegration";

                        // Build display text with page name, username, and instance name
                        const displayName = pageInfo?.pageName || pageInfo?.username || selectedIntegration.label;
                        const showUsername = isSocialIntegration && pageInfo?.username && pageInfo?.pageName;
                        const showInstanceName = isSocialIntegration && integration?.instanceName;

                        return (
                          <Box display="flex" alignItems="center" gap={1}>
                            {logo && (
                              <Avatar
                                src={logo}
                                sx={{ width: 24, height: 24 }}
                                variant={pagePicture ? "circular" : "square"}
                              />
                            )}
                            <Typography variant="body2">
                              {displayName}
                              {showUsername && ` (@${pageInfo.username})`}
                              {showInstanceName && ` - ${integration.instanceName}`}
                            </Typography>
                          </Box>
                        );
                      }}
                    >
                      {options.map((option: any) => {
                        // Get the full integration object to access the type and page info
                        let integration: any = null;
                        if (setting.name === "wordpressIntegration") {
                          integration = wordpressIntegrations.find(
                            (wp) => wp.id === option.value
                          );
                        } else if (setting.name === "linkedinIntegration") {
                          integration = linkedinIntegrations.find(
                            (li) => li.id === option.value
                          );
                        } else if (setting.name === "facebookIntegration") {
                          integration = facebookIntegrations.find(
                            (fb) => fb.id === option.value
                          );
                        } else if (setting.name === "instagramIntegration") {
                          integration = instagramIntegrations.find(
                            (ig) => ig.id === option.value
                          );
                        }

                        const integrationType = integration?.type || "";
                        const pageInfo = integration?.pageInfo;
                        const pagePicture =
                          pageInfo?.pagePicture || pageInfo?.profilePicture;
                        const logo =
                          pagePicture || getIntegrationLogo(integrationType);
                        const isSocialIntegration =
                          setting.name === "facebookIntegration" ||
                          setting.name === "instagramIntegration";

                        return (
                          <MenuItem key={option.value} value={option.value}>
                            <Box display="flex" alignItems="center" gap={1.5}>
                              {logo && (
                                <Avatar
                                  src={logo}
                                  sx={{ width: 32, height: 32 }}
                                  variant={pagePicture ? "circular" : "square"}
                                />
                              )}
                              <Box>
                                <Typography variant="body2">
                                  {pageInfo?.pageName ||
                                    pageInfo?.username ||
                                    option.label}
                                </Typography>
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={0.5}
                                  flexWrap="wrap"
                                >
                                  {pageInfo?.username && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      @{pageInfo.username}
                                    </Typography>
                                  )}
                                  {isSocialIntegration && integration?.instanceName && (
                                    <Chip
                                      label={integration.instanceName}
                                      size="small"
                                      sx={{
                                        height: 16,
                                        fontSize: "0.65rem",
                                      }}
                                    />
                                  )}
                                  {pageInfo?.pageCategory && (
                                    <Chip
                                      label={pageInfo.pageCategory}
                                      size="small"
                                      sx={{
                                        height: 16,
                                        fontSize: "0.65rem",
                                      }}
                                    />
                                  )}
                                  {!isSocialIntegration && integrationType && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {integrationType
                                        .charAt(0)
                                        .toUpperCase() +
                                        integrationType.slice(1)}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                );
              } else {
                // Default to checkbox for other settings
                return (
                  <FormControlLabel
                    key={setting.name}
                    control={
                      <Checkbox
                        checked={
                          safeValue[contentType.name]?.subSettings?.[
                            setting.name
                          ] || false
                        }
                        onChange={(e) =>
                          onChange({
                            ...safeValue,
                            [contentType.name]: {
                              ...safeValue[contentType.name],
                              subSettings: {
                                ...safeValue[contentType.name]?.subSettings,
                                [setting.name]: e.target.checked,
                              },
                            },
                          })
                        }
                        onClick={(e) => e.stopPropagation()}
                        name={setting.name}
                        color="primary"
                      />
                    }
                    label={setting.label}
                  />
                );
              }
            })}
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  );
};
